import React, { useState } from "react";
import { ValueForYear } from "../../graphQL/types/shared";
import { fontFamily } from "../Utils";

export const strokeColor = "rgb(76,76,76)";

// Map of background colors to their WCAG-compliant text colors
const getTextColor = (backgroundColor: string) => {
  switch (backgroundColor) {
    case "#2363A0": // dark blue
    case "#B12231": // dark red
      return "#FFFFFF";
    case "#91C2DC": // light blue
    case "#F9E9C4": // light beige
    case "#F0A486": // light orange/salmon
      return strokeColor;
    default:
      return strokeColor; // fallback to dark text
  }
};

interface Props {
  id: string;
  name: string;
  eciYearRange: ValueForYear[];
  eciRankYearRange: ValueForYear[];
  yMultiplier: number;
  xMultiplier: number;
  topBuffer: number;
  leftBuffer: number;
  minYear: number;
  minECI: number;
  getColor: (value: number) => string;
  highlighted: boolean;
  spotlighted: boolean;
  spotlightOn: boolean;
  allCountriesData: Array<{
    eciYearRange: ValueForYear[];
    eciRankYearRange: ValueForYear[];
  }>;
}

const Datapoint = (props: Props) => {
  const {
    id,
    name,
    eciYearRange,
    eciRankYearRange,
    yMultiplier,
    xMultiplier,
    minYear,
    minECI,
    getColor,
    topBuffer,
    leftBuffer,
    highlighted,
    spotlighted,
    spotlightOn,
    allCountriesData,
  } = props;

  const [hovered, setHovered] = useState<boolean>(false);

  const yearPoints = eciRankYearRange.map(({ quantity, year }, i) => {
    if (!quantity || !year || year < minYear) {
      return null;
    }
    const rank = quantity;

    const y = yMultiplier * (rank - 1) + topBuffer;
    const x = xMultiplier * (year - minYear) + leftBuffer;
    const textY = yMultiplier * rank + yMultiplier + topBuffer;
    const eci = eciYearRange[i].quantity || minECI;
    const fill = getColor(eci);
    const stroke = hovered || highlighted || spotlighted ? strokeColor : fill;
    let strokeWidth: string;
    if (spotlightOn && !spotlighted && !hovered) {
      strokeWidth = "0";
    } else {
      strokeWidth = hovered || highlighted || spotlighted ? "1" : "1.25";
    }
    const textX = x + xMultiplier / 2;
    const eciYPos = xMultiplier > 42 ? textY + 6 : textY + 5;
    const eciText =
      xMultiplier > 42 ? (
        <>ECI: {eci.toFixed(2)}</>
      ) : (
        <>
          <tspan x={textX} dx={0} dy={0}>
            ECI:
          </tspan>
          <tspan x={textX} dx={0} dy={10}>
            {eci.toFixed(2)}
          </tspan>
        </>
      );

    // Get text color based on cell 2 ranks above
    let textColor = strokeColor;
    if (rank > 2) {
      // Find the country that is 2 ranks above (where the text will be rendered)
      const countryAboveRank = rank - 2;
      // Find the country that has this rank in the same year
      const countryAbove = allCountriesData.find((country) =>
        country.eciRankYearRange.some(
          (r) => r.year === year && r.quantity === countryAboveRank
        )
      );
      if (countryAbove) {
        // Get that country's ECI value for this year
        const countryAboveEci =
          countryAbove.eciYearRange.find((e) => e.year === year).quantity ||
          minECI;
        const backgroundColor = getColor(countryAboveEci);
        textColor = hovered ? strokeColor : getTextColor(backgroundColor);
      }
    }

    const eciElm =
      highlighted && !spotlighted ? null : (
        <text
          x={textX}
          y={eciYPos}
          textAnchor="middle"
          style={{
            fill: textColor,
            fontSize: 10,
            pointerEvents: "none",
            fontFamily,
          }}
        >
          {eciText}
        </text>
      );

    const text =
      hovered || highlighted || spotlighted ? (
        <>
          <text
            x={textX}
            y={y - 4}
            textAnchor="middle"
            style={{
              fill: textColor,
              fontSize: 12,
              pointerEvents: "none",
              fontFamily,
            }}
          >
            {rank}
          </text>
          {eciElm}
        </>
      ) : null;
    let connectionLine: React.ReactElement<any> | null;
    if (
      (hovered || highlighted || spotlighted) &&
      i !== eciRankYearRange.length - 1
    ) {
      const nextValue = eciRankYearRange[i + 1];
      const nextRank = nextValue.quantity;
      if (!nextRank || !nextValue.year || nextValue.year < minYear) {
        connectionLine = null;
      } else {
        const x1 = x;
        const y1 = y;
        const y2 = yMultiplier * (nextRank - 1) + topBuffer;
        const x2 =
          xMultiplier * (nextValue.year - minYear) + xMultiplier + leftBuffer;
        connectionLine = (
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={strokeColor}
            strokeWidth="1"
          />
        );
      }
    } else {
      connectionLine = null;
    }
    // Make highlight adjustment proportional to rectangle size
    const highlightSizeAdjust = yMultiplier * 0.1; // 10% of rectangle height
    const yPos = hovered || highlighted ? y - highlightSizeAdjust : y;
    const height =
      hovered || highlighted
        ? yMultiplier + highlightSizeAdjust * 2
        : yMultiplier;
    const onMouseEnter = () => setHovered(true);
    return (
      <g
        onMouseEnter={onMouseEnter}
        onMouseLeave={() => setHovered(false)}
        key={id + x + y + year + quantity}
      >
        <rect
          x={x}
          y={yPos}
          width={xMultiplier}
          height={height}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
        />
        {text}
        {connectionLine}
      </g>
    );
  });
  let label: React.ReactElement<any> | null;
  if (hovered || highlighted || spotlighted) {
    const { quantity, year } = eciRankYearRange[0];
    if (!quantity || !year) {
      label = null;
    } else {
      const rank = quantity;
      const y = yMultiplier * (rank - 1) + topBuffer + 8;
      const x = xMultiplier * (year - minYear) + xMultiplier + leftBuffer + 4;
      let fontSize = 13;
      const nameParts = name.split(" ");
      const tspans = nameParts.map((part, i) => {
        const text = i === 0 ? rank + ". " + part : part;
        if (part.length > 13) {
          fontSize = 10;
        } else if (part.length > 11) {
          fontSize = 11;
        } else if (part.length > 9 && rank > 100) {
          fontSize = 12;
        }
        let dx: number;
        if (i !== 0) {
          if (rank < 10) {
            dx = 12;
          } else if (rank < 100) {
            dx = 18;
          } else {
            dx = 26;
          }
        } else {
          dx = 0;
        }
        return (
          <tspan x={x} dx={dx} dy={i !== 0 ? 15 : 0} key={text}>
            {text}
          </tspan>
        );
      });
      label = (
        <text
          x={x}
          y={y}
          style={{
            fill: strokeColor,
            fontSize,
            pointerEvents: "none",
            fontFamily,
          }}
        >
          {tspans}
        </text>
      );
    }
  } else {
    label = null;
  }
  const hoveredClassName = hovered ? "hovered" : "";
  const highlightedClassName = highlighted ? " highlighted" : "";
  const spotlightedClassName = spotlightOn && spotlighted ? " spotlighted" : "";
  return (
    <g
      className={hoveredClassName + highlightedClassName + spotlightedClassName}
      style={{
        opacity: spotlightOn && !spotlighted && !hovered ? 0.3 : 1,
      }}
    >
      {label}
      {yearPoints}
    </g>
  );
};

export default Datapoint;
